import { WebAppState } from "@/enums/WebAppState";
import type { NewChatMessageBody, SnapshotChunkMessage, Incident  } from '../types';
import { videoOptions } from '@/config';
import { useIncident} from '@/composables/useIncident';
import { hubConnectionManager } from "@/signalR/hubConnectionManager";
import type { HubConnection } from "@microsoft/signalr";

export function useIncidentHub() {
  const { incidentId } = useIncident();

  const hub = hubConnectionManager(
    `${videoOptions.EMS_SERVER_URL}/hub/incident/${incidentId}`
  );

  async function startConnection(): Promise<HubConnection> {
    const isNewConnection = !hub.isStarted();
    const connection = await hub.start();
    if (isNewConnection) {
      await setWebAppState(WebAppState.AppOpened);
    }

    return connection;
  }

  async function getWebAppIncident(): Promise<Incident | null> {
    return await hub.invoke("GetWebAppIncident");
  }

  async function sendChatMessage(message: NewChatMessageBody) {
    await hub.invoke("SendChatMessage", message);
  }

  async function sendSnapshotChunk(chunk: SnapshotChunkMessage) {
    await hub.invoke("SendSnapshotChunk", chunk);
  }

  async function sendChatPreview(messageText: string) {
    return await hub.invoke("SendCallerChatTextMessagePreview", messageText);
  }

  async function translateText(text: string): Promise<string | null> {
    return await hub.invoke("TranslateToLanguageWithSourceLanguageDetection", text, navigator.language);
  }

  async function getDispatcherChatTextMessagePreview(): Promise<string | null> {
    return await hub.invoke("GetDispatcherChatTextMessagePreview");
  }

  async function setWebAppState(newState: WebAppState) {
    console.log("Setting WebAppState to:", newState);
    return await hub.invoke("SetWebAppState", newState);
  }

  return {
    startSignalRConnection: startConnection,
    sendChatPreview,
    sendChatMessage,
    sendSnapshotChunk,
    getWebAppIncident,
    translateText,
    getDispatcherChatTextMessagePreview,
    setWebAppState
  };
}
